import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  List,
  Spin,
  message,
  Typography,
  Space,
  Divider,
  Switch,
} from "antd";
import { DownloadOutlined, FileTextOutlined } from "@ant-design/icons";
import Api from "api";
import MatchCategoryTag from "components/Collection/MatchCategoryTag";

const { Title, Text, Paragraph } = Typography;

interface UpdateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionId: string;
  collectionName: string;
}

interface ReportLine {
  recordNumber: number;
  totalRecords: number;
  originalName: string;
  matchedName: string;
  matchType: string;
  category?: string | null;
  changed?: boolean;
  description: string;
}

const UpdateReportModal: React.FC<UpdateReportModalProps> = ({
  isOpen,
  onClose,
  collectionId,
  collectionName,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<any>(null);
  // Most records match as uploaded; show the ones whose name changed unless asked for all.
  const [changedOnly, setChangedOnly] = useState<boolean>(true);
  const shownLines: ReportLine[] = (reportData?.reportLines || []).filter(
    (item: ReportLine) => !changedOnly || item.changed !== false
  );

  useEffect(() => {
    if (isOpen && collectionId) {
      fetchUpdateReport();
    }
  }, [isOpen, collectionId]);

  const fetchUpdateReport = async () => {
    setLoading(true);
    try {
      const { data } = await Api.get(`/collections/${collectionId}/update_report`);
      setReportData(data);
    } catch (error: any) {
      console.error("Failed to fetch update report:", error);
      message.error("Failed to load update report");
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!reportData || !reportData.reportLines) return;

    const lines = shownLines.map((item: ReportLine) => {
      return `Record ${item.recordNumber} of ${item.totalRecords}: ${item.originalName} was updated to ${item.matchedName} (${item.matchType})`;
    });

    const reportText = [
      `Update Report for Collection: ${reportData.collectionName}`,
      `Collection Type: ${reportData.collectionType}`,
      `Total Records: ${reportData.totalRecords}`,
      changedOnly ? `Showing: ${shownLines.length} records whose name changed` : `Showing: all records`,
      `Generated: ${new Date().toLocaleString()}`,
      "",
      "=" .repeat(80),
      "",
      ...lines,
    ].join("\n");

    const blob = new Blob([reportText], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${reportData.collectionName}_update_report.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    message.success("Report downloaded successfully!");
  };

  const renderReportItem = (item: ReportLine) => {
    return (
      <List.Item>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text strong>
            Record {item.recordNumber} of {item.totalRecords}:
          </Text>
          <Text>
            <Text type="secondary">{item.originalName}</Text> → <Text type="success">{item.matchedName}</Text>
            <MatchCategoryTag category={item.category} label={item.category ? item.matchType : null} />
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            ({item.description})
          </Text>
        </Space>
      </List.Item>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          Updates Made from Original List
        </Space>
      }
      visible={isOpen}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="download" icon={<DownloadOutlined />} onClick={downloadReport} disabled={!reportData}>
          Download Report
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
      bodyStyle={{
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
      }}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 20 }}>Loading update report...</Paragraph>
        </div>
      ) : reportData ? (
        <>
          <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
            <Title level={5}>{reportData.collectionName}</Title>
            <Text type="secondary">
              Collection Type: <Text strong>{reportData.collectionType}</Text>
            </Text>
            <Text type="secondary">
              Total Records: <Text strong>{reportData.totalRecords}</Text>
            </Text>
            <Space>
              <Switch checked={changedOnly} onChange={setChangedOnly} size="small" />
              <Text>Only records whose name changed</Text>
            </Space>
          </Space>

          <Divider />

          <List
            dataSource={shownLines}
            renderItem={renderReportItem}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ${changedOnly ? "changed records" : "records"}`,
            }}
          />
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Text type="secondary">No data available</Text>
        </div>
      )}
    </Modal>
  );
};

export default UpdateReportModal;
